import { Button, Card, Input, message, Modal, Space, Spin, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Request2 } from 'src/common/Request2'
import api from 'src/common/api'
import { ERROR_CODES } from 'src/helpers/constants'
import { useStore } from 'src/hooks'
import { CheckCircleOutlined, LockOutlined, ExclamationCircleOutlined } from '@ant-design/icons'

const { Title, Text, Paragraph } = Typography

const TwoFactorSetup = observer(() => {
   const AuthStore = useStore('AuthStore')
   const { data_getUserInfo } = AuthStore

   const [loading, setLoading] = useState(false)
   const [status, setStatus] = useState<{ enabled: boolean } | null>(null)
   const [setupData, setSetupData] = useState<{ secret: string; qrCodeDataUrl: string } | null>(null)
   const [otpCode, setOtpCode] = useState('')
   const [verifying, setVerifying] = useState(false)
   const [disableOtp, setDisableOtp] = useState('')
   const [disabling, setDisabling] = useState(false)
   const [disableModalVisible, setDisableModalVisible] = useState(false)

   const fetchStatus = async () => {
      try {
         setLoading(true)
         const result = await Request2.getWithToken(api.twoFactor.status)
         if (result) {
            setStatus(result)
         }
      } catch (error) {
         // ignore
      } finally {
         setLoading(false)
      }
   }

   useEffect(() => {
      if (data_getUserInfo?.id) {
         fetchStatus()
      }
   }, [data_getUserInfo?.id])

   const handleSetup = async () => {
      try {
         setLoading(true)
         const result = await Request2.postWithToken({}, api.twoFactor.setup)
         // postWithToken trả về nguyên response { code, message, data }
         if (result?.data) {
            setSetupData(result.data)
         }
      } catch (error) {
         message.error('Không thể tạo mã QR. Vui lòng thử lại.')
      } finally {
         setLoading(false)
      }
   }

   const handleVerifySetup = async () => {
      if (!otpCode || otpCode.length !== 6) {
         message.warning('Vui lòng nhập mã OTP 6 chữ số')
         return
      }
      try {
         setVerifying(true)
         const result = await Request2.postWithToken({ token: otpCode }, api.twoFactor.verifySetup)
         if (result?.data?.enabled) {
            message.success('Bật 2FA thành công!')
            setSetupData(null)
            setOtpCode('')
            setStatus({ enabled: true })
         }
      } catch (error) {
         message.error('Mã OTP không đúng. Vui lòng thử lại.')
      } finally {
         setVerifying(false)
      }
   }

   const handleOpenDisable = () => {
      setDisableOtp('')
      setDisableModalVisible(true)
   }

   const handleConfirmDisable = async () => {
      if (!disableOtp || disableOtp.length !== 6) {
         message.warning('Vui lòng nhập mã OTP 6 chữ số')
         return
      }
      try {
         setDisabling(true)
         const result = await Request2.postWithToken({ token: disableOtp }, api.twoFactor.disable)
         if (result?.data && !result.data.enabled) {
            message.success('Đã tắt 2FA')
            setStatus({ enabled: false })
            setDisableOtp('')
            setDisableModalVisible(false)
         }
      } catch (error) {
         message.error('Mã OTP không đúng. Không thể tắt 2FA.')
      } finally {
         setDisabling(false)
      }
   }

   if (loading && !setupData) {
      return (
         <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin size="large" />
         </div>
      )
   }

   return (
      <div style={{ padding: 24, maxWidth: 600 }}>
         <Title level={3}>
            <LockOutlined style={{ marginRight: 8 }} />
            Xác thực 2 bước (2FA)
         </Title>

         {status?.enabled ? (
            <Card>
               <Space direction="vertical" size={16}>
                  <Space>
                     <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                     <Text strong style={{ fontSize: 16 }}>
                        2FA đang được bật
                     </Text>
                  </Space>
                  <Paragraph type="secondary">
                     Tài khoản của bạn đang được bảo vệ bằng xác thực 2 bước. Mỗi lần đăng nhập sẽ cần nhập mã OTP từ
                     ứng dụng Authenticator.
                  </Paragraph>
                  <Button danger onClick={handleOpenDisable} loading={disabling}>
                     Tắt 2FA
                  </Button>
               </Space>
            </Card>
         ) : setupData ? (
            <Card title="Thiết lập 2FA">
               <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Paragraph>
                     <strong>Bước 1:</strong> Quét mã QR bên dưới bằng ứng dụng Google Authenticator hoặc Authy.
                  </Paragraph>
                  <div style={{ textAlign: 'center' }}>
                     <img
                        src={setupData.qrCodeDataUrl}
                        alt="QR Code"
                        style={{ width: 200, height: 200, border: '1px solid #f0f0f0', borderRadius: 8 }}
                     />
                  </div>
                  <Paragraph type="secondary" style={{ textAlign: 'center' }}>
                     Hoặc nhập mã thủ công: <Text code copyable>{setupData.secret}</Text>
                  </Paragraph>
                  <Paragraph>
                     <strong>Bước 2:</strong> Nhập mã OTP 6 chữ số từ ứng dụng để xác nhận.
                  </Paragraph>
                  <Input
                     maxLength={6}
                     placeholder="Nhập mã OTP"
                     value={otpCode}
                     onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                     onPressEnter={handleVerifySetup}
                     style={{ fontSize: 18, textAlign: 'center', letterSpacing: 8, maxWidth: 200 }}
                  />
                  <Space>
                     <Button type="primary" onClick={handleVerifySetup} loading={verifying}>
                        Xác nhận bật 2FA
                     </Button>
                     <Button
                        onClick={() => {
                           setSetupData(null)
                           setOtpCode('')
                        }}
                     >
                        Huỷ
                     </Button>
                  </Space>
               </Space>
            </Card>
         ) : (
            <Card>
               <Space direction="vertical" size={16}>
                  <Paragraph>
                     Xác thực 2 bước (2FA) giúp bảo vệ tài khoản admin bằng cách yêu cầu mã OTP từ ứng dụng
                     Authenticator mỗi khi đăng nhập.
                  </Paragraph>
                  <Paragraph type="secondary">
                     Ứng dụng hỗ trợ: Google Authenticator, Authy, Microsoft Authenticator, v.v.
                  </Paragraph>
                  <Button type="primary" onClick={handleSetup} loading={loading}>
                     Thiết lập 2FA
                  </Button>
               </Space>
            </Card>
         )}

         <Modal
            title="Tắt xác thực 2 bước"
            visible={disableModalVisible}
            onCancel={() => setDisableModalVisible(false)}
            onOk={handleConfirmDisable}
            okText="Xác nhận tắt"
            cancelText="Huỷ"
            okButtonProps={{ danger: true, loading: disabling }}
            destroyOnClose
         >
            <Space align="start" style={{ marginBottom: 8 }}>
               <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20, marginTop: 2 }} />
               <Paragraph style={{ marginBottom: 0 }}>
                  Nhập mã OTP từ ứng dụng Authenticator để xác nhận tắt 2FA:
               </Paragraph>
            </Space>
            <Input
               maxLength={6}
               placeholder="Nhập mã OTP"
               value={disableOtp}
               onChange={(e) => setDisableOtp(e.target.value.replace(/\D/g, ''))}
               onPressEnter={handleConfirmDisable}
               style={{ fontSize: 18, textAlign: 'center', letterSpacing: 8, marginTop: 8 }}
            />
         </Modal>
      </div>
   )
})

export default TwoFactorSetup
